import * as React from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { Modal, Typography, Input, Radio, Spin, Result } from 'antd'
import { endCollectionAction, CashoutMethod, collectFundsAction } from './types'
import { either, option, record, task } from 'fp-ts'
import { FormattedMessage } from 'react-intl'
import { memoed, useOrganization } from '@library/react-toolkit'
import { pipe } from 'fp-ts/lib/function'
import Amount from '../Amount'
import styled from 'styled-components'
import NymAutocomplete from '../NymAutocomplete'
import { cashoutMethodsSupportedAssets } from './api'

const AmountEmphasis = styled.span`
  font-size: 18px;
  font-weight: bolder;
`

const RadioGroup = styled(Radio.Group)`
  display: block;
  margin-top: ${p => p.theme.paddingUnit * 2}px;
`

const Radio_ = styled(Radio)`
  display: block;
  height: 48px;
  font-size: 18px;
  * > input,
  * > .ant-select {
    width: 50%;
    float: right;
  }
`

const AmountDisplay = styled.div`
  font-size 16px;
`

const DestinationPrompt = styled.div`
  margin-top: ${p => p.theme.paddingUnit * 2}px;
  font-size 16px;
`

type SupportedAssets = Record<CashoutMethod, string[]> | 'Loading' | 'Failed'

const isSupported = (sas: SupportedAssets, m: CashoutMethod, u: string) =>
  sas !== 'Loading' && sas !== 'Failed' && sas[m].includes(u)

const CashoutModal = () => {
  const organization = useOrganization()
  const visible = useSelector(s =>
    option.isSome(s.wallet.collectFundsState.selectedAsset)
  )
  const asset = useSelector(s =>
    pipe(
      s.wallet.collectFundsState.selectedAsset,
      option.getOrElse(() => '')
    )
  )
  const amount = useSelector(s =>
    pipe(
      s.wallet.collectFundsState.selectedAsset,
      option.chain(u => record.lookup(u, memoed.extract(s.balance).balance)),
      option.getOrElse(() => 0)
    )
  )
  const dispatch = useDispatch()

  const [method, selectMethod] = React.useState<CashoutMethod | undefined>(
    undefined
  )
  const [nym, selectNym] = React.useState<string | undefined>(undefined)
  const [rib, setRib] = React.useState<string | undefined>(undefined)
  const [paypalID, setPaypalID] = React.useState<string | undefined>(undefined)
  const [paymeeID, setPaymeeID] = React.useState<number | undefined>(undefined)

  const [supportedAssets, setSupportedAssets] =
    React.useState<SupportedAssets>('Loading')

  React.useEffect(() => {
    if (visible) {
      selectMethod(undefined)
      selectNym(undefined)
      setRib(organization?.rib?.toString())
      setSupportedAssets('Loading')
      pipe(
        cashoutMethodsSupportedAssets(),
        task.map(
          either.fold(() => setSupportedAssets('Failed'), setSupportedAssets)
        )
      )()
    }
  }, [visible])

  return (
    <Modal
      zIndex={1}
      visible={visible}
      onCancel={() => dispatch(endCollectionAction())}
      destroyOnClose={true}
      width="100%"
      style={{ maxWidth: '800px' }}
      title={
        <Typography.Title style={{ marginBottom: 0 }} level={3}>
          <FormattedMessage id="collectionTitle" />
        </Typography.Title>
      }
      onOk={() => {
        if (method === 'TRANSFER' && !!nym) {
          dispatch(collectFundsAction({ method, unitID: asset, nym }))
        } else if (method === 'KIKO' && !!rib) {
          dispatch(collectFundsAction({ method, unitID: asset, rib }))
        } else if (method === 'PAYPAL' && !!paypalID) {
          dispatch(collectFundsAction({ method, unitID: asset, paypalID }))
        } else if (method === 'PAYMEE' && !!paymeeID) {
          dispatch(collectFundsAction({ method, unitID: asset, paymeeID }))
        }
      }}
      okButtonProps={{
        disabled:
          !method ||
          (method === 'TRANSFER' && !nym) ||
          (method === 'KIKO' && !rib),
      }}
    >
      <div>
        <AmountDisplay>
          <FormattedMessage id="collectionNotify" />{' '}
          <AmountEmphasis>
            <Amount amount={amount} unit={asset} />
          </AmountEmphasis>
        </AmountDisplay>
        <Spin spinning={supportedAssets === 'Loading'}>
          {supportedAssets === 'Failed' ? (
            <Result status="error" />
          ) : (
            <>
              <DestinationPrompt>
                <FormattedMessage id="cashoutDestination" />
              </DestinationPrompt>
              <RadioGroup onChange={e => selectMethod(e.target.value)}>
                <Radio_
                  value="KIKO"
                  disabled={!isSupported(supportedAssets, 'KIKO', asset)}
                >
                  <FormattedMessage id="wireCashout" />
                  {method === 'KIKO' ? (
                    <Input value={rib} onChange={e => setRib(e.target.value)} />
                  ) : null}
                </Radio_>
                <Radio_
                  value="PAYPAL"
                  disabled={!isSupported(supportedAssets, 'PAYPAL', asset)}
                >
                  <FormattedMessage id="paypalCashout" />
                  {method === 'PAYPAL' ? (
                    <Input
                      value={paypalID}
                      onChange={e => setPaypalID(e.target.value)}
                    />
                  ) : null}
                </Radio_>
                <Radio_
                  value="PAYMEE"
                  disabled={!isSupported(supportedAssets, 'PAYMEE', asset)}
                >
                  <FormattedMessage id="paymeeCashout" />
                  {method === 'PAYMEE' ? (
                    <Input
                      type="number"
                      value={paymeeID}
                      onChange={e =>
                        setPaymeeID(parseInt(e.target.value, 10) ?? undefined)
                      }
                    />
                  ) : null}
                </Radio_>
                <Radio_
                  value="TRANSFER"
                  disabled={!isSupported(supportedAssets, 'TRANSFER', asset)}
                >
                  <FormattedMessage id="transferCashout" />
                  {method === 'TRANSFER' ? (
                    <NymAutocomplete onSelect={selectNym} />
                  ) : null}
                </Radio_>
              </RadioGroup>
            </>
          )}
        </Spin>
      </div>
    </Modal>
  )
}

export default CashoutModal
